import { Formiz, useForm } from "@formiz/core";
import { isMaxLength, isMinLength, isNotEmptyString, isRequired } from "@formiz/validations";
import { compareErrorCode, IAuthenticatedUser, useUserInfo } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { UserErrorType } from "@next-platform/packages-ts-types";
import { Spacer, Spinner } from "@nextui-org/react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { LocalizedLink } from "@/components";
import CheckboxField from "@/components/common/CheckboxField";
import FormField from "@/components/common/FormField";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import { Container, Grid, SpicyButton, SpicyInput, Text } from "@/components/customComponents";
import useApi from "@/components/useApi";
import { ROUTES } from "@/constants";
import useEnsureUserProfile from "@/features/user/utils/useEnsureUserProfile";
import { isUsernameValid } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import useAuth from "../../services/useAuth";

const logger = appLogger.withTag("SignUp");

const SignUp = () => {
  const userForm = useForm();
  const { onUserUpdateProfileV2, isLoading: isSubmitting } = useUserInfo();
  const { invalidateFields } = userForm;
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const navigate = useNavigate();
  const { jsonApiService, callApi } = useApi();
  const { setAuthenticatedUser } = useEnsureUserProfile();

  const { username: profileUsername, name: profileName } = useUserInfo();
  const [errMessage, setErrMessage] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(false);
  const [debouncedUsername] = useDebounce(userForm.values?.username, 500);
  const isPartner = getIsPartner();

  useEffect(() => {
    if (isPartner) {
      navigate(ROUTES.externalSignup);
    }
  }, [isPartner, navigate]);

  useEffect(() => {
    const checkUsernameAvailability = async (username: string) => {
      try {
        await callApi(jsonApiService.checkUsername, username).then((data) => {
          const { isAvailable } = (data ?? { isAvailable: false }) as { isAvailable: boolean };
          if (!isAvailable) {
            invalidateFields({ username: "Username is not available" });
          }
          setIsUsernameAvailable(isAvailable);
          setIsCheckingUsername(false);
        });
      } catch (error) {
        logger.error("Error in checkUsernameAvailability", error);
        // Handle error
      }
    };
    if (isUsernameValid(debouncedUsername) && debouncedUsername && debouncedUsername.length >= 3) {
      setIsCheckingUsername(true);
      checkUsernameAvailability(debouncedUsername);
    }
  }, [debouncedUsername, callApi, jsonApiService.checkUsername, invalidateFields]);
  const usernameHelper = useMemo(() => {
    if (!userForm.values.username) {
      return {
        text: "",
        color: "",
      };
    }

    if (userForm.values.username.length < 3) {
      return {
        text: "Username is invalid",
        color: "error",
      };
    }
    const isValid = isUsernameValid(userForm.values.username);

    if (!isValid) {
      return {
        text: isValid ? "Valid" : "Username is invalid",
        color: isValid ? "success" : "error",
      };
    }
    if (isCheckingUsername) {
      return {
        text: "Checking...",
        color: "info",
        icon: <Spinner size="sm" />,
      };
    }

    if (isUsernameAvailable) {
      return {
        text: "Available",
        color: "",
        icon: <Icon icon={IconVariants.CHECK_CIRCLE} color="#17C964" size={IconSize.SMALL} />,
      };
    }
    if (!isUsernameAvailable) {
      return {
        text: "Not Available",
        color: "",
        icon: <Icon icon={IconVariants.CLOSE_CIRCLE} color="#9f1942" size={IconSize.SMALL} />,
      };
    }
    return {};
  }, [userForm.values.username, isCheckingUsername, isUsernameAvailable]);

  if (!isAuthenticated && isAuthLoading) {
    return <Spinner />;
  }
  if (!isAuthenticated) {
    navigate("/");
  }

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload = {
      username: userForm.values.username,
      name: userForm.values.name,
    };
    const { error, errorPayload } = await onUserUpdateProfileV2(payload);

    if (error) {
      const hasUsernameAlreadyTaken = compareErrorCode(errorPayload.errorCode, UserErrorType.USERNAME_DUPLICATED);
      if (hasUsernameAlreadyTaken) {
        setIsUsernameAvailable(false);
        invalidateFields({ username: error });
      }
      return;
    }

    setAuthenticatedUser({ ...user } as IAuthenticatedUser);
    navigate("/");
    setTimeout(() => {
      setErrMessage("");
    }, 2000);
  };

  return (
    <Container>
      <PageStaticMetaTags pageId="signup" />
      <Text h3>Welcome Aboard</Text>
      <Spacer y={6} />
      <Text h4>{`Here's some basic rules:`}</Text>
      <Spacer y={1} />
      <ul className="m-3 ml-5">
        <li className="mb-4">
          <Text>- No hate speech</Text>
        </li>
        <li className="mb-4">
          <Text>- No underage</Text>
        </li>
        <li className="mb-4">
          <Text>- No incest</Text>
        </li>
      </ul>
      <Formiz onValidSubmit={submitForm} connect={userForm} initialValues={{ username: profileUsername, name: profileName }}>
        <form onSubmit={submitForm}>
          <FormField
            component={SpicyInput}
            variant="primary_small_short"
            color="primary"
            title="Username"
            name="username"
            desc="The username that would appear on your public characters."
            aria-labelledby="username"
            isClearable
            maxLength={20}
            endContent={usernameHelper.icon}
            toLowerCase={true}
            validations={[
              {
                rule: isRequired(),
                message: "Username can't be empty",
              },
              {
                rule: isMinLength(3),
                message: "Username is too short",
              },
              {
                rule: isMaxLength(20),
                message: "Username should be less then 20 characters ",
              },
              {
                rule: (value: string) => isUsernameValid(value),
                message: "Username contain invalid characters",
              },
            ]}
          />
          <Spacer y={5} />

          <FormField
            component={SpicyInput}
            variant="primary_small_short"
            color="primary"
            title="Name"
            name="name"
            desc="How you want to be called when chatting."
            aria-labelledby="name"
            maxLength={20}
            validations={[
              {
                rule: isRequired(),
                message: "Name can't be empty",
              },
              {
                rule: isNotEmptyString(),
                message: "Name can't be empty",
              },
              {
                rule: isMinLength(3),
                message: "Name should be of atleast 3 characters",
              },
            ]}
          />
          <Spacer y={5} />
          <Grid>
            <CheckboxField
              size="sm"
              name="ageConfirmationCheckbox"
              aria-labelledby="checkbox-age"
              validations={[
                {
                  rule: (v: boolean) => v === true,
                  message: "You must confirm that you are 18 years or older.",
                },
              ]}
            >
              <Text variant={"md"}>I confirm that I am 18 years or older.</Text>
            </CheckboxField>
          </Grid>
          <Spacer />
          <Grid className="flex gap-[10px] items-center">
            <CheckboxField
              size="sm"
              name="checkbox"
              aria-labelledby="checkbox-terms"
              validations={[
                {
                  rule: (v: boolean) => v === true,
                  message: "You must agree with the terms and conditions.",
                },
              ]}
            >
              <Text variant={"md"}>
                You agree to be bound by our{" "}
                <LocalizedLink className="text-link no-underline" to={"/terms"} aria-label="terms-of-service">
                  Terms of Service{" "}
                </LocalizedLink>
                and{" "}
                <LocalizedLink className="text-link no-underline" to={"/privacy"} aria-label="privacy-policy">
                  Privacy Policy
                </LocalizedLink>
                .
              </Text>
            </CheckboxField>
          </Grid>
          <Spacer y={0.5} />
          <div className="text-green-600 text-left mr-[16px] p-[10px] h-[35px]">
            <Text>{errMessage}</Text>
          </div>
          <div>
            <SpicyButton
              variant="primary_large"
              color="primary_strong"
              type="submit"
              aria-labelledby="save"
              isDisabled={!userForm?.isValid || isSubmitting}
            >
              {isSubmitting ? <Spinner color="current" size="sm" /> : "Start Chatting"}
            </SpicyButton>
          </div>
        </form>
      </Formiz>
    </Container>
  );
};

export default SignUp;
